/** 手机触控：独立操作按钮使用至少44px点击区域；桌面保持原紧凑布局，业务权限、提交与跳转逻辑不变。 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useApp } from "../../../context/AppContext";

import { T } from "../../../lib/i18n";

/* 多行模式：输入框按内容自动增高，1 行起步，超过 6 行（约 134px）内部滚动 */
const MULTILINE_MAX_H = 134;

let CACHE = null;
const useCandidates = (api, candidates) => {
  const [list, setList] = useState(CACHE || []);
  useEffect(() => {
    if (Array.isArray(candidates)) {
      setList(candidates);
      return;
    }
    if (CACHE) { setList(CACHE); return; }
    api.get("/design/v3/mention-candidates").then(({ data }) => { CACHE = data; setList(data); }).catch(() => {});
  }, [api, candidates]);
  return list;
};

/** 评论输入框：输入 @ 弹出同事候选（设计部优先，可搜索，↑↓/Enter 选择），插入 `@姓名 `
 *  multiline=true 时渲染多行文本框（按内容自动增高），Enter 发送 / Shift+Enter 换行
 *  disabled=true 时整个输入框不可编辑（2026-10-19：上一阶段未完成 → 本阶段会话禁止发言，
 *  仅禁发送按钮不够，用户仍可先打字再被拦回，故把输入本身也一起锁住） */
export const MentionInput = ({ value, onChange, onSubmit, placeholder, tid, className, autoFocus, multiline, dropUp = false, disableMentions = false, disabled = false, candidates }) => {
  const { api } = useApp();
  const cands = useCandidates(api, candidates);
  const ref = useRef(null);
  const [q, setQ] = useState(null);
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!multiline || !el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight + 2, MULTILINE_MAX_H)}px`;
  }, [value, multiline]);
  const matches = useMemo(() => {
    if (q == null) return [];
    const k = q.toLowerCase();
    return cands.filter((c) => !k || c.name.toLowerCase().includes(k) || (c.nameZh || "").includes(q) || c.username.toLowerCase().includes(k)).slice(0, 8);
  }, [q, cands]);
  const detect = (v, caret) => {
    // 阶段会话已改为「负责人芯片」选择提醒对象（2026-10-19 用户需求），
    // 这里直接关闭 @ 检测，避免输入 @ 时又弹出与芯片重复的候选下拉。
    if (disableMentions) return;
    const m = /(?:^|\s)@([^\s@]*)$/.exec(v.slice(0, caret));
    setQ(m ? m[1] : null); setIdx(0);
  };
  const pick = (c) => {
    const el = ref.current; const caret = el ? el.selectionStart : value.length;
    const before = value.slice(0, caret).replace(/@[^\s@]*$/, `@${c.name} `);
    const next = before + value.slice(caret);
    onChange(next); setQ(null);
    setTimeout(() => { if (el) { el.focus(); el.setSelectionRange(before.length, before.length); } }, 0);
  };
  const onKey = (e) => {
    if (q != null && matches.length) {
      if (e.key === "ArrowDown") { e.preventDefault(); setIdx((i) => (i + 1) % matches.length); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setIdx((i) => (i - 1 + matches.length) % matches.length); return; }
      if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); pick(matches[idx]); return; }
      if (e.key === "Escape") { setQ(null); return; }
    }
    // 多行模式下 Shift+Enter 交给浏览器换行，其余 Enter 仍为发送
    if (e.key === "Enter" && !(multiline && e.shiftKey)) { e.preventDefault(); onSubmit?.(); }
  };
  const shared = { ref, value, placeholder, autoFocus, autoComplete: "off", onKeyDown: onKey, onBlur: () => setTimeout(() => setQ(null), 150), disabled };
  return (
    <div className={`relative ${multiline ? "w-full basis-full" : "flex-1"}`}>
      {multiline ? (
        <textarea {...shared} data-testid={tid} rows={1} className={`${className} block w-full resize-none leading-5 overflow-y-auto`}
          onChange={(e) => { onChange(e.target.value); detect(e.target.value, e.target.selectionStart); }} />
      ) : (
        <input {...shared} type="text" data-testid={tid} className={className}
          onChange={(e) => { onChange(e.target.value); detect(e.target.value, e.target.selectionStart); }} />
      )}
      {q != null && matches.length > 0 && (
        /* 2026-10-19 阶段会话输入框固定在抽屉底部，向下展开会被底边裁掉；
           这里按调用方传入的方向展开，底部输入框改为向上弹出。
           候选最多 8 人；max-h-72 刚好能一次显示完，避免底部最后一项还要再滚一下。 */
        <div className={`absolute left-0 ${dropUp ? "bottom-full mb-1" : "top-full mt-1"} z-30 w-72 max-h-72 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg py-1`} data-testid={`${tid}-mentions`}>
          {matches.map((c, i) => (
            <button key={c.username} type="button" data-testid={`${tid}-mention-${c.username}`} onMouseDown={(e) => { e.preventDefault(); pick(c); }}
              className={"min-h-11 min-w-11 md:min-h-0 md:min-w-0 " + (`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 ${i === idx ? "bg-violet-50 text-violet-800" : "text-slate-700 hover:bg-slate-50"}`)}>
              <span className="font-semibold">{c.name}</span>{c.nameZh && c.nameZh !== c.name && <span className="text-slate-400">{c.nameZh}</span>}
              <span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded-full ${c.isDesign ? "bg-violet-100 text-violet-700" : "bg-slate-100 text-slate-500"}`}>{c.isDesign ? T("设计部") : c.role}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/** 评论正文：@姓名 高亮紫色（优先用后端 mentions 列表，其次通用 @词） */
export const MentionText = ({ text, mentions }) => {
  const names = (mentions || []).filter(Boolean).sort((a, b) => b.length - a.length);
  const rx = names.length ? new RegExp(`(@(?:${names.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")}))`, "g") : /(@[\w\u4e00-\u9fa5]+)/g;
  return <>{String(text || "").split(rx).map((part, i) => (i % 2 === 1 ? <span key={i} className="text-violet-700 font-semibold bg-violet-50 rounded px-0.5" data-testid="dv3-mention">{part}</span> : part))}</>;
};
